/*
 * This module uses table markup entered by a user with the WYSIWYG editor and transforms it into a staff or schedule
 * widget.
 *
 * For the staff widgets, it is assumed that each row of the table represents a staff member. The textContent of the
 * first cell in a row will be used for the name, the textContent of the second cell will be used for the credentials,
 * and the third cell should contain an img element. Only the src of this img element is used.
 *
 * For the schedule widget, it is assumed that the first cell in each column of the table is the date, and that each
 * cell/td after it in the same column is a schedule item for that date.
 */
define(["wtm_libs"], (wtm) => {
    // Do not run when loaded in iframe (to avoid issues with admin).
    if (window.self !== window.top) return;

    wtm.widgets.staffWidgets.forEach(widgetOptions => {
        try {
            createStaffWidgets(widgetOptions);
        } catch (e) {
            console.error(e);
        }
    });

    wtm.widgets.scheduleWidgets.forEach(widgetOptions => {
        try {
            createScheduleWidget(widgetOptions);
        } catch (e) {
            console.error(e);
        }
    });

    /*
     * Returns a click handler that will make the day with the provided index
     * visible and hide all the others.
     */
    function createActiveToggler(visibleIdx, days) {
        return () => {
            days.forEach((day, idx) => {
                if (idx === visibleIdx) {
                    day.navButton.classList.add("active");
                    day.container.classList.add("active");
                } else {
                    day.navButton.classList.remove("active");
                    day.container.classList.remove("active");
                }
            });
        }
    }

    function getClassMaker(prefix) {
        return str => prefix + "-" + str;
    }

    function getSelectorMaker(prefix) {
        return str => "." + prefix + "-" + str;
    }

    /*
     * Provided a table element, returns a list of objects representing staff members.
     *
     * Each row of a provided table element containing at least a name will add an associated staff member to the list.
     *
     * The textContent of the first child of the tr provides the name, the textContent of the second provides the
     * credentials, and the third is checked for an img element. If present, the src of the image is used.
     */
    function getStaff(tableElem) {
        const trElems = Array.prototype.slice.call(tableElem.querySelectorAll("tr"));
        const rows = [];
        trElems.forEach(tr => {
            const tdElems = Array.prototype.slice.call(tr.children);
            let row = {};
            if (tdElems.length === 0) {
                return;
            }
            const name = tdElems[0] ? tdElems[0].textContent : "";
            if (!name) return;
            const credentials = tdElems[1] ? tdElems[1].textContent : "";
            const staffImage = tdElems[2].querySelector("img");
            const imgSrc = staffImage ? staffImage.src : null;
            row = {
                imgSrc: imgSrc,
                name: name,
                credentials: credentials
            };
            if ( tdElems.length >= 4) {
                const filterText = Array.prototype.slice.call(tdElems[3].querySelectorAll('p'));
                row.description = "";
                filterText.forEach(p => {
                    row.description += "<p>" + p.textContent + "</p>";
                });
            }
            if ( tdElems.length >= 5) {
                row.cv = tdElems[4].textContent;
            }
            rows.push(row);
        });
        return rows;
    }

    /*
     * Takes an options object and creates a staff widget using classes that begin with the classPrefix
     * property of the options. Currently no other options are used.
     */
    function createStaffWidgets(options) {
        const prefix = options.classPrefix || "event";

        // Convenience functions to avoid having to manually concatenate the custom prefix each time it is used
        const makeClass = getClassMaker(prefix);
        const makeSelector = getSelectorMaker(prefix);

        const staffContainers = Array.prototype.slice.call(document.querySelectorAll(makeSelector("staff-container")));
        if (staffContainers.length === 0) return;

        staffContainers.forEach(staffContainer => {
            // Hide the staffContainer until/unless a widget is built
            staffContainer.style.display = "none";

            const staffListElem = staffContainer.querySelector(makeSelector("staff-list"));
            if (!staffListElem) return;
            const tableSelector = makeSelector("staff-list") + " > table";
            const staffTable = staffContainer.querySelector(tableSelector);
            if (!staffTable) return;

            const staffList = getStaff(staffTable);

            // Remove the original table markup, as it is no longer needed
            staffTable.parentNode.removeChild(staffTable);

            staffList.forEach(staff => {
                const staffElem = document.createElement("div");
                staffElem.classList.add(makeClass("staff-member"));
                staffListElem.appendChild(staffElem);

                const staffImage = document.createElement("img");
                staffImage.alt = staff.name + ", " + staff.credentials;
                staffImage.src = staff.imgSrc || "/view/assets/images/staff_placeholder.jpg";
                staffImage.classList.add(makeClass("staff-member-image"));
                staffElem.appendChild(staffImage);

                const staffTextContainer = document.createElement("div");
                staffTextContainer.classList.add(makeClass("staff-member-text"));
                staffElem.appendChild(staffTextContainer);

                const staffName = document.createElement("div");
                staffName.textContent = staff.name;
                staffName.classList.add(makeClass("staff-member-name"));
                staffTextContainer.appendChild(staffName);

                const staffCredentials = document.createElement("div");
                staffCredentials.textContent = staff.credentials;
                staffCredentials.classList.add(makeClass("staff-member-credentials"));
                staffTextContainer.appendChild(staffCredentials);

                if (staff.description) {
                    const staffDescription = document.createElement("div");
                    staffDescription.innerHTML = staff.description;
                    staffDescription.classList.add(makeClass("staff-member-description"));
                    staffListElem.classList.add(makeClass("staff-member__description"));
                    if (staff.cv) {
                        const staffCV = document.createElement("a");
                        staffCV.href = staff.cv;
                        staffCV.innerText = "View CV";
                        staffCV.classList.add(makeClass("staff-member-cv"));
                        staffDescription.appendChild(staffCV);
                    }
                    staffListElem.appendChild(staffDescription);
                }
            });

            // Widget created; unhide it:
            staffContainer.style.display = "block";

            // Use this class if you need to override the display property of the visible staff members
            staffContainer.classList.add("staff-container-visible");

            // staffContainer.style.opacity = "1";
        });

    }

    /*
     * Provided a table element, getColumns will return an array for which each element is an array containing the
     * innerHTML strings for the cells in a single column of the table.
     *
     * Only td elements are supported, and the function assumes that each row contains the same number of td elements
     * in order to assign the cells to the correct columns. This condition should hold if the table is created and
     * edited using the WYSIWYG.
     */
    function getColumns(tableElem) {
        // Array.prototype.slice.call is used instead of Array.from for compatibility with IE11
        const rows = Array.prototype.slice.call(tableElem.querySelectorAll("tr"));
        const colCount = rows[0].childElementCount;
        const columns = [];
        for (let i = 0; i < colCount; i++) {
            columns.push([]);
        }
        rows.forEach((row) => {
            const cells = Array.prototype.slice.call(row.querySelectorAll("td"));
            cells.forEach((cell, cellIdx) => {
                columns[cellIdx].push(cell.innerHTML);
            });
        });
        return columns;
    }

    function colsToDays(columns) {
        return columns.map(column => {
            const dateText = column[0];
            const scheduleData = column.slice(1);
            return {
                date: dateText,
                scheduleData: scheduleData
            };
        });
    }

    /*
     * Takes an options object and creates a schedule widget using classes that begin with the classPrefix
     * property of the options. Currently no other options are used.
     */
    function createScheduleWidget(options) {
        const prefix = options.classPrefix || "event";

        // Convenience functions to avoid having to manually concatenate the custom prefix each time it is used
        const makeClass = getClassMaker(prefix);
        const makeSelector = getSelectorMaker(prefix);

        const scheduleContainer = document.querySelector(makeSelector("schedule-container"));
        if (!scheduleContainer) return;
        // Hide the scheduleContainer until/unless a schedule is built
        scheduleContainer.style.display = "none";
        const scheduleElem = document.querySelector(makeSelector("schedule"));
        const scheduleNav = document.querySelector(makeSelector("schedule-nav"));
        if (!scheduleElem || !scheduleNav) return;

        const tableSelector = makeSelector("schedule") + "> table";
        const scheduleTable = document.querySelector(tableSelector);
        if (!scheduleTable) return;

        const columns = getColumns(scheduleTable);
        const days = colsToDays(columns);

        // Remove the original table markup now that it is no longer needed.
        scheduleTable.parentNode.removeChild(scheduleTable);

        /*
         * Here we create the lists that will represent the structure and add references to the corresponding navButton
         * and container elements for each day object.
         */
        days.forEach((day, idx) => {
            const navButton = document.createElement("div");
            navButton.textContent = getNavButtonTextContent(idx);
            navButton.classList.add(makeClass("schedule-button"));
            navButton.addEventListener("click", createActiveToggler(idx, days));
            if (navButton.textContent.trim().toLowerCase() !== 'hide') {
                scheduleNav.appendChild(navButton);
            }
            day.navButton = navButton;

            const dayContainer = document.createElement("div");
            dayContainer.classList.add(makeClass("schedule-day"));
            scheduleElem.appendChild(dayContainer);
            day.container = dayContainer;

            const header = document.createElement("h3");
            header.textContent = day.date;
            dayContainer.appendChild(header);

            const ul = document.createElement("ul");
            dayContainer.appendChild(ul);
            day.scheduleData.forEach(scheduleItem => {
                if (scheduleItem.trim() === "") return; // do not create empty schedule items.

                const li = document.createElement("li");
                li.innerHTML = scheduleItem;
                ul.appendChild(li);
            });

            if (idx === 0) {
                dayContainer.classList.add("active");
                navButton.classList.add("active");
            }
        });

        /*
         * Checks for defined schedule nav buttons and gets current index
         * else returns default day counter
         */
        function getNavButtonTextContent(idx) {
            if (document.getElementsByClassName('schedule-nav-buttons')) {
                const navButtons = document.querySelectorAll('.schedule-nav-buttons tr:first-of-type td');
                return navButtons[idx] ? navButtons[idx].innerText : 'Day ' + (idx + 1);
            } else {
                return 'Day ' + (idx + 1);
            }
        }

        /*
         * Create button and associated functionality to toggle whether only a fixed height of the widget is visible,
         * or the full contents.
         */
        const showMoreButtonContainer = document.createElement("div");
        scheduleContainer.appendChild(showMoreButtonContainer);
        showMoreButtonContainer.classList.add(makeClass("schedule-show-more-button-container"));

        const showMoreButton = document.createElement("button");
        showMoreButtonContainer.appendChild(showMoreButton);
        showMoreButton.textContent = "Show More";
        showMoreButton.classList.add(makeClass("schedule-show-more-button"));

        const fixedHeight = "640px";
        scheduleElem.style.height = fixedHeight;
        showMoreButton.addEventListener("click", () => {
            showMoreButton.classList.toggle("open");
            showMoreButton.textContent = showMoreButton.textContent === "Show More"
                ? "Show Less"
                : "Show More";
            scheduleElem.style.height = scheduleElem.style.height === fixedHeight
                ? "auto"
                : fixedHeight;
        });

        // Unhide the schedule container
        scheduleContainer.style.display = "block";
    }
});

